import {
  Component, ChangeDetectionStrategy, ChangeDetectorRef, Input,
  ViewChild, OnDestroy, OnChanges, SimpleChanges,
  Output, EventEmitter,
} from '@angular/core';
import * as _ from 'lodash';
import { Observable ,  Subject } from 'rxjs';

import { BrandService } from 'shared/brand/brand.service';
import { AdvancedSearchService } from 'shared/search/advanced-search/advanced-search.service';
import { FitmentsService } from 'shared/fitments/fitments.service';
import { ActiveFilterModel } from 'shared/search/active-filters/active-filter.model';
import { PART_ATTRIBUTES_FILTER_TYPE, PADB_FILTER_TYPE, CUSTOM_FILTER_TYPE } from 'shared/search/active-filters/active-filter-types';
import { defaultOptionValue } from 'shared/form/select/option-helpers';

import { filterPlaceholders} from 'shared/search/advanced-search/filter-placeholders';
import { myFitmentsOptions } from 'shared/fitments/fitments-options';
import { USER_ATTR_OPT } from 'shared/search/part-attributes-search/attribute-options';
import {
  REGION_TYPE, VEHICLE_TYPE, YEAR_START_TYPE, YEAR_END_TYPE,
} from 'shared/search/advanced-search/filter-types';
import { BaseAdvancedSearchComponent } from 'shared/search/advanced-search/_base-advanced-search.component';
import { PartAttributesSearchComponent } from 'shared/search/part-attributes-search/part-attributes-search.component';
import { VehicleSearchComponent } from 'shared/search/vehicle-search/vehicle-search.component';
import { VehicleAttributesSearchComponent } from 'shared/search/vehicle-attributes-search/vehicle-attributes-search.component';


@Component({
  moduleId: module.id,
  selector: 'product-advanced-search',
  templateUrl: 'product-advanced-search.component.html',
  styleUrls: ['product-advanced-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductAdvancedSearchComponent extends BaseAdvancedSearchComponent {

  @Input() status: string = 'working';

  @Output() enterClicked: EventEmitter<any> = new EventEmitter();

  @ViewChild(PartAttributesSearchComponent, {static: false})
  partAttributesComponent: PartAttributesSearchComponent | void;

  @ViewChild(VehicleSearchComponent, {static: true})
  declare vehicleSearch: any | void;

  @ViewChild(VehicleAttributesSearchComponent, {static: false})
  declare vehicleAttributes: any | void;

  private excludeAttributes: Array<string> = [
    'year', 'make', 'model', 'qty', 'position', 'mfrLabel', 'notes',
  ];

  constructor(
    public brandService: BrandService,
    protected ref: ChangeDetectorRef,
    private fitmentsService: FitmentsService,
    private advancedSearchService: AdvancedSearchService,
  ) {
    super(brandService, ref);
    this.filterPlaceholders = filterPlaceholders;
  }

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  get showAddAnotherBtn(): boolean {
    return true;
  }

  get partAttributesFilters(): Array<ActiveFilterModel> {
    return _.filter(this.filters, (filter: any) => {
      return filter.type === PART_ATTRIBUTES_FILTER_TYPE;
    });
  }

  get partAttributesAllFilters(): Array<ActiveFilterModel> { // part, padb, custom attributes
    return _.filter(this.filters, (filter: any) => {
      return filter.type === PART_ATTRIBUTES_FILTER_TYPE ||
        filter.type === PADB_FILTER_TYPE ||
        filter.type === CUSTOM_FILTER_TYPE;
    });
  }

  get partAttributes(): Array<ActiveFilterModel> | void {
    return this.partAttributesComponent && this.partAttributesComponent.partAttributes;
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  onPartAttributeOptionsFetch($event: any) {
    const searchModel = this.filterSearchModel;
    let filterName = $event.attribute.attribute_name;
    if (_.has($event, 'fromField')) {
      _.assign(searchModel, {[$event.fromField]: $event.attribute.option});
      filterName = $event.formGroup.get('attributeOption').value;
    }
    if (filterName === 'createdDate' || filterName === 'modifiedDate') {
      // no need to request avaliable dates
      if (this.partAttributesComponent instanceof PartAttributesSearchComponent) {
        this.partAttributesComponent.setAttributeStructure(
          $event.attribute, $event.formGroup, $event.data
        );
      }
      return;
    }
    this.showSpinner(true);
    this.fetchAttributeOptions(filterName)
    .subscribe((data: Array<any>) => {
      $event.data = data ? data : [];
      if (this.partAttributesComponent instanceof PartAttributesSearchComponent) {
        this.partAttributesComponent.setAttributeStructure(
          $event.attribute, $event.formGroup, $event.data
        );
      }

      this.showSpinner(false);
    });
  }

  onPartAttributeRemove(_id: any) {
    _.remove(this.activeFilters, {_id});
    this.ref.markForCheck();
  }

  onEnterClicked() {
    this.runSearch.emit();
  }

  onSearchClick() {
    this.runSearch.emit();
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  public onPartAttributesFetchProcess(inProcess: boolean) {
    this.showSpinner(inProcess);
  }

  public removeFilter(filter: ActiveFilterModel) {
    super.removeFilter(filter);
    if (_.get(filter, 'type') === PART_ATTRIBUTES_FILTER_TYPE && filter.data) {
      if (this.partAttributesComponent instanceof PartAttributesSearchComponent) {
        this.partAttributesComponent.removeFilter(filter);
      }
    }
  }

  public resetAllData(): void {
    if (this.partAttributesComponent) {
        this.partAttributesComponent.resetFormGroup();
    }
    super.resetAllData();
  }

  public clearFilters(): void {
    super.clearFilters();
    this.resetPartAttributes();
  }

  /*******************************************
   * COMPONENT PROTECTED METHODS
   *******************************************/

  protected resetPartAttributes() {
    if (this.partAttributesComponent instanceof PartAttributesSearchComponent) {
      this.partAttributesComponent.resetAttributes();
    }
  }

  protected initAdvancedSearch(): void {
    super.initAdvancedSearch();
    if (!this.partAttributesAllFilters.length) {
      this.resetPartAttributes();
    } else {
      if (this.partAttributesComponent instanceof PartAttributesSearchComponent) {
        this.partAttributesComponent.addFiltersAttributes(this.partAttributesAllFilters);
      }
    }
  }

  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/

  private fetchOptions(filterName: string, filterSearchModel: any): Observable<any> {
    let searchModel = filterSearchModel;
    if (filterName === REGION_TYPE || filterName === VEHICLE_TYPE) {
      // do not filter regions & vehicle types
      searchModel = _.omit(searchModel, ['vehicleType', 'region']);
    }
    if (filterName.toLowerCase().indexOf('year') !== -1) {
      filterName = 'year';
    } else {
      // if maxYear is not selected, then set maxYear = minYear
      if (!searchModel[YEAR_END_TYPE]) {
        searchModel = _.assign(
          _.clone(searchModel),
          {[YEAR_END_TYPE]: searchModel[YEAR_START_TYPE]}
        );
      }
    }
    return this.fetchAttributeOptions(filterName, searchModel);
  }

  private fetchAttributes(): void {
    this.showSpinner(true);
    this.advancedSearchService.fetchOptionsFieldsList(
      this.status, this.brandId
    )
    .subscribe((data: any) => {
      if (data) {
        // remove Product Attributes from result - it will be handled manually in code
        const filteredData = _.filter(data, (item: any) => item.attribute_name !== 'productAttribute');

        this.attributesList = filteredData;

        this.partAttributesOptions = _.filter(filteredData, (item: any) => {
          return _.has(item, 'item_detail_specific');
        });
        // Add Attribute option https://speedlab.atlassian.net/browse/SPD2-4299
        this.partAttributesOptions.push({
          title: 'Attributes',
          attribute_name: USER_ATTR_OPT,
          option: USER_ATTR_OPT,
          is_user_attribute: true,
          check_for_null: true,
        });
      }
      this.showSpinner(false);
    });
  }

  // fetch part attributes filter
  private fetchAttributeOptions(filterName: string, selectedFilters?: any): Observable<any> {
    return this.advancedSearchService.fetchAttributeOptions(
      this.status,
      this.brandId,
      filterName,
      selectedFilters
    );
  }

  private fetchAttributeGroups(): void {
    this.showSpinner(true);
    this.advancedSearchService.fetchGroupedFieldsList(
      this.status,
      this.brandId,
    )
    .subscribe((data: any) => {
      this.setAttributeGroups(data);
      this.showSpinner(false);
    });
  }
}
